/* The structured block Brill emits when she locks an offer in the Offer Ladder chat.
 *
 * WHY THIS EXISTS, AND WHY IT ISN'T PROSE
 * The Accelerator's locking mechanism wraps ordinary paragraphs in
 * `<<<OUTPUT_START:…>>>` and we read the fields back out of that prose. That
 * approach produced three separate wrong-data bugs in `offerStore.js` — a
 * cost-of-pain figure read as a billing interval, a label stored as an offer name,
 * a payment plan missed entirely — because the layout is a guideline Brill follows
 * loosely, not a contract. See `offerStore.test.js`.
 *
 * So the ladder does not guess. Brill states each field on its own labelled line
 * and we read exactly those. Anything she can't state, she leaves out, and the
 * field stays empty for the coach to fill — the same rule as the pull.
 *
 * The shape follows the `<<<TESTIMONIAL_SUBMITTED>>>` block already in the app
 * (delimiters plus `LABEL: value` lines), so this is one more instance of an
 * existing pattern rather than a second convention to learn.
 *
 *   <<<OFFER_LOCKED>>>
 *   NAME: The Signal Sprint
 *   TYPE: trust
 *   SUMMARY: A five-day challenge that gets her first paid client booked.
 *   AMOUNT: $97
 *   MODE: flat
 *   INTERVAL: month
 *   PAYMENT_PLAN: unknown
 *   DETAIL: Longer notes — what's included, how it runs, why the price.
 *   <<<OFFER_END>>>
 */

import { EMPTY_PRICING } from "./offerStore";

const BLOCK = /<<<OFFER_LOCKED>>>([\s\S]*?)<<<OFFER_END>>>/;

// Only the four types the ladder actually stores. Anything else falls to `other`
// rather than creating a type no `OFFER_TYPES` entry can render.
const TYPES = ["trust", "core", "continuation", "other"];

/* Labels are read as `LABEL:` at the start of a line, and a value runs until the
   next label or the end of the block — so DETAIL can be several paragraphs without
   needing to be escaped. */
const FIELDS = ["NAME", "TYPE", "SUMMARY", "AMOUNT", "MODE", "INTERVAL", "PAYMENT_PLAN", "DETAIL"];

function readField(body, label, { paragraphs = false } = {}) {
  const others = FIELDS.filter((f) => f !== label).join("|");
  /* No `m` flag: with it, `$` matches every line end and a value would stop at the
     first line break instead of running to the next label. `(?:^|\n)` does the
     line-start job that `^` would otherwise need `m` for. */
  const re = new RegExp(`(?:^|\\n)[ \\t]*${label}[ \\t]*:([\\s\\S]*?)(?=\\n[ \\t]*(?:${others})[ \\t]*:|$)`, "i");
  const m = re.exec(body);
  if (!m) return "";
  const raw = m[1].replace(/\*/g, "");
  // Single-line fields collapse entirely. DETAIL keeps its blank-line paragraph
  // breaks — it's the long-form field, and running it together would be unreadable.
  if (!paragraphs) return raw.replace(/\s+/g, " ").trim();
  return raw
    .split(/\n[ \t]*\n/)
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n\n");
}

/** Does this reply contain a lock block at all? */
export function hasOfferBlock(text) {
  return BLOCK.test(text || "");
}

/**
 * The fields Brill locked, or null when there's no block.
 *
 * Returns partial data happily — a block with only a NAME is still a usable
 * result. Never invents a value for a field she left out.
 */
export function parseOfferBlock(text) {
  const m = BLOCK.exec(text || "");
  if (!m) return null;
  const body = m[1];

  const rawType = readField(body, "TYPE").toLowerCase();
  const type = TYPES.includes(rawType) ? rawType : "other";

  const amount = readField(body, "AMOUNT").replace(/\s/g, "");
  const rawMode = readField(body, "MODE").toLowerCase();
  const rawInterval = readField(body, "INTERVAL").toLowerCase();
  const rawPlan = readField(body, "PAYMENT_PLAN").toLowerCase();

  const pricing = { ...EMPTY_PRICING };
  if (amount) {
    pricing.amount = amount;
    pricing.mode = rawMode === "recurring" ? "recurring" : "flat";
    pricing.interval = /^(year|yearly|annual|annually)$/.test(rawInterval) ? "year"
      : /^(week|weekly)$/.test(rawInterval) ? "week" : "month";
    // Tri-state, same as the pull: absence means "not answered", which is not
    // the same as "no". Only an explicit yes sets it.
    pricing.paymentPlan = /^(yes|true)$/.test(rawPlan) ? true
      : /^(no|false)$/.test(rawPlan) ? false : null;
  }

  return {
    name: readField(body, "NAME"),
    type,
    summary: readField(body, "SUMMARY"),
    detail: readField(body, "DETAIL", { paragraphs: true }),
    pricing,
  };
}

/**
 * The reply with the block removed — what the coach reads in the chat.
 *
 * The fields are rendered as form inputs beside the conversation, so leaving the
 * block inline would show her the same content twice with delimiters around it.
 */
export function stripOfferBlock(text) {
  return (text || "").replace(new RegExp(BLOCK.source, "g"), "").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Fold a locked block onto the offer being edited.
 *
 * Her own edits win over an empty field — Brill omitting SUMMARY must never blank
 * a summary the coach already wrote. `type` is left alone entirely: she chose it
 * from the Add menu, and the guide is told which type it's building for.
 */
export function applyOfferBlock(draft, block) {
  if (!block) return draft;
  const summary = [block.summary, block.detail].filter(Boolean).join("\n\n");
  return {
    ...draft,
    name: block.name || draft.name,
    summary: summary || draft.summary,
    pricing: block.pricing.amount ? block.pricing : draft.pricing,
  };
}
